import {BadRequestException, GatewayTimeoutException, HttpException, NotFoundException} from "@/infrastructure/http";
import debounce from "debounce";
import {proxy, ref} from "valtio";

/**
 * Threshold for detecting network timeout (in milliseconds).
 *
 * When a server returns 504 Gateway Timeout, the browser may drop the connection
 * before receiving the HTTP response. In this case, JavaScript only receives
 * a generic "TypeError: Failed to fetch" without access to the actual status code.
 *
 * To distinguish between a real timeout and other network errors (CORS, DNS, etc.),
 * we check if the request duration exceeded this threshold. If "Failed to fetch"
 * occurs after a long request, it's likely a 504 timeout.
 */
const NETWORK_TIMEOUT_THRESHOLD_MS = 25_000; // 25 seconds

// Global Symbol for setupDebounce (accessible across modules)
const INIT = Symbol("INIT");

type QueryStatus = "pending" | "fulfilled" | "rejected";

interface QueryStateOptions {
    debounceDelay?: number;
}

class QueryStateClass<T, TParams extends Array<unknown> = []> {
    status: QueryStatus | null = null;
    data: T | null = null;
    error: Error | null = null;
    private abortController: AbortController | null = null;
    private runQueue: Promise<void> = Promise.resolve();
    private debouncedExecuteInternal: ((...params: TParams) => void) | null = null;
    private readonly fetcher: (signal: AbortSignal, ...params: TParams) => Promise<T>;
    private readonly debounceDelay?: number;

    constructor(fetcher: (signal: AbortSignal, ...params: TParams) => Promise<T>, options: QueryStateOptions = {}) {
        this.fetcher = fetcher;
        this.debounceDelay = options.debounceDelay;
        // Debounce setup will happen AFTER instance is assigned to proxied parent
    }

    // Setup debounce after instance is part of proxy (for correct `this` context)
    [INIT](): void {
        if (this.debounceDelay && this.debounceDelay > 0) {
            this.debouncedExecuteInternal = debounce((...params: TParams) => void this.executeInternal(...params), this.debounceDelay);
        }
    }

    // Getters for convenience
    get isPending(): boolean {
        return this.status === "pending";
    }

    get isFulfilled(): boolean {
        return this.status === "fulfilled";
    }

    get isRejected(): boolean {
        return this.status === "rejected";
    }

    get isIdle(): boolean {
        return this.status === null;
    }

    // Error type checks
    get hasValidationError(): boolean {
        return this.error instanceof BadRequestException;
    }

    get hasNotFoundError(): boolean {
        return this.error instanceof NotFoundException;
    }

    get hasHttpError(): boolean {
        return this.error instanceof HttpException;
    }

    get hasTimeoutError(): boolean {
        return this.error instanceof GatewayTimeoutException;
    }

    // Type-safe error body access
    // Returns unknown to allow different APIs to have different error body formats
    // Users should cast to their specific error body type when needed
    get validationError(): unknown {
        if (this.error instanceof BadRequestException && this.error.errorBody) {
            return this.error.errorBody;
        }
        return null;
    }

    // Error message access
    get errorMessage(): string | null {
        if (this.error instanceof HttpException) {
            return this.error.message;
        }
        return this.error?.message || null;
    }

    // Status code access
    get statusCode(): number | null {
        if (this.error instanceof HttpException) {
            return this.error.statusCode;
        }
        return null;
    }

    // Generic error body access
    get errorBody(): unknown {
        if (this.error instanceof HttpException) {
            return this.error.errorBody;
        }
        return null;
    }

    // Public execute method (with optional debounce)
    execute(...params: TParams): Promise<void> | void {
        this.status = "pending";
        if (this.debouncedExecuteInternal) {
            this.debouncedExecuteInternal(...params);
            return undefined;
        } else {
            // Direct execution without debounce - return promise for testing
            return this.executeInternal(...params);
        }
    }

    // Public immediate execute method (always bypasses debounce)
    executeImmediate(...params: TParams): Promise<void> {
        this.status = "pending";
        return this.executeInternal(...params);
    }

    // Internal execute method
    private async executeInternal(...params: TParams): Promise<void> {
        const previousRun = this.runQueue;
        let resolveNext!: () => void;
        this.runQueue = new Promise((resolve) => {
            resolveNext = resolve;
        });

        if (this.abortController) {
            this.abortController.abort();
        }

        await previousRun.catch(() => {
            /* empty */
        });

        this.error = null;
        this.abortController = ref(new AbortController());
        const currentController = this.abortController;
        const signal = currentController.signal;
        const startTime = Date.now();

        try {
            const result = await this.fetcher(signal, ...params);

            // Check if this request was cancelled (e.g. via cancel()); runQueue ensures no new controller until we finish
            if (signal.aborted) {
                return;
            }

            this.data = result;
            this.status = "fulfilled";
            this.abortController = null;
        } catch (error) {
            // Ignore abort errors; do not set status = null — next run in queue already has status = "pending"
            if (error instanceof Error && error.name === "AbortError") {
                return;
            }

            // Handle other errors
            if (currentController === this.abortController) {
                const elapsedTime = Date.now() - startTime;

                // Detect network timeout: TypeError("Failed to fetch") + long request duration = likely 504
                const isNetworkTimeout =
                    error instanceof TypeError && error.message === "Failed to fetch" && elapsedTime >= NETWORK_TIMEOUT_THRESHOLD_MS;

                this.error = isNetworkTimeout
                    ? new GatewayTimeoutException(`Request timed out after ${Math.round(elapsedTime / 1000)}s`, undefined, error)
                    : error instanceof Error
                      ? error
                      : new Error(String(error));

                this.status = "rejected";
                this.abortController = null;

                // Always throw error after storing in state (allows try/catch)
                throw this.error;
            }
        } finally {
            resolveNext();
        }
    }

    // Cancel current request
    cancel(): void {
        if (this.debouncedExecuteInternal && "clear" in this.debouncedExecuteInternal) {
            (this.debouncedExecuteInternal as {clear: () => void}).clear();
        }

        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
        this.status = null;
    }

    // Reset state
    reset(): void {
        this.cancel();
        this.data = null;
        this.error = null;
        this.status = null;
    }
}

export type QueryStateInstance<T, TParams extends Array<unknown> = []> = QueryStateClass<T, TParams>;

// Factory function
export function QueryState<T, TParams extends Array<unknown> = []>(
    fetcher: (signal: AbortSignal, ...params: TParams) => Promise<T>,
    options?: QueryStateOptions,
): QueryStateInstance<T, TParams> {
    const instance = proxy(new QueryStateClass(fetcher, options));
    // Setup debounce after proxying (for correct `this` context)
    instance[INIT]();
    return instance;
}
